import { Box, Input, Tab } from '@hawkins/components';
import { IconCheckmark, IconPencil } from '@hawkins/assets';
import { useState, useEffect, useRef } from 'react';
import { PseudoIconButton } from './PseudoIconButton';
import styles from './EditableTab.module.css';

interface ValidationState {
  status: 'error' | undefined;
  message: string;
}

interface EditableTabProps {
  title: string;
  onTitleChange: (newTitle: string) => void;
  onValidationChange?: (hasError: boolean) => void;
}

export const EditableTab = ({
  title,
  onTitleChange,
  onValidationChange,
}: EditableTabProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(title);
  const [validationState, setValidationState] =
    useState<ValidationState>({
      status: undefined,
      message: '',
    });

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const hasNonAscii = /[^\x20-\x7E]/.test(editValue);
    const isTooLong = editValue.length > 50;
    const hasError = hasNonAscii || isTooLong;

    if (hasNonAscii) {
      setValidationState({
        status: 'error',
        message: 'Label cannot contain non-ASCII characters',
      });
    } else if (isTooLong) {
      setValidationState({
        status: 'error',
        message: 'Label cannot exceed 50 characters',
      });
    } else {
      setValidationState({
        status: undefined,
        message: '',
      });
    }

    if (onValidationChange) {
      onValidationChange(hasError);
    }
  }, [editValue, onValidationChange]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      // Use setTimeout to ensure the input is fully rendered before focusing
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    }
  }, [isEditing]);

  const handleStartEdit = () => {
    setEditValue(title);
    setIsEditing(true);
  };

  const handleSave = () => {
    if (validationState.status !== 'error' && editValue.trim()) {
      onTitleChange(editValue.trim());
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditValue(title);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const editingInput = (
    <Box onClick={handleInputClick} className={styles.tabContainer}>
      <Input
        ref={inputRef}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleSave}
        className={styles.tabInput}
      />
      <PseudoIconButton
        onClick={handleSave}
        ariaLabel="Complete Edit"
        icon={IconCheckmark}
      />
    </Box>
  );

  const tabLabel = (
    <Box className={styles.tabContainer} ref={containerRef}>
      <span className={styles.tabLabel}>{title}</span>
      <PseudoIconButton
        onClick={handleStartEdit}
        ariaLabel={`Edit ${title}`}
        icon={IconPencil}
      />
    </Box>
  );

  return (
    <Tab key="framingDecision1">
      {isEditing ? editingInput : tabLabel}
    </Tab>
  );
};
